import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { css } from '@emotion/react';

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import OTPInput from 'components/field/OTPInput';
import PasswordInput from 'components/field/PasswordInput';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import Image from 'components/global/Image';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';

import axios, { axiosAuth } from 'services/axios';
import { AUTH, MFA } from 'services/consts';

import { setUserDetails } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';

import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';

import * as S from './TwoFactorModal.styled';

const TwoFactorDisableModal = NiceModal.create(() => {
	const modal = useModal();
	const [value, setValue] = useState([null, null, null, null, null, null]);
	const { showError, showSuccess } = useAlert();
	const [password, setPassword] = useState(null);
	const email = useSelector((state) => state.user.email);
	const [tab, setTab] = useState(1);
	const [loading, setLoading] = useState(false);
	const [useBackupCode, setUseBackupCode] = useState(false);
	const dispatch = useDispatch();

	const login = async () => {
		setLoading(true);
		try {
			const response = await axiosAuth.post(AUTH.LOGIN, {
				email,
				password
			});

			if (response.status === 200) {
				setTab(3);
			} else {
				showError({
					message: 'errorMsg.incorrectPassword'
				});
				AppLogger.error(response);
			}
		} catch (error) {
			showError({
				message: 'errorMsg.incorrectPassword'
			});
			AppLogger.error(error);
		} finally {
			setLoading(false);
		}
	};

	const submit = () => {
		setLoading(true);

		const code = value.join('');
		axios
			.post(MFA.DISABLE_MFA, {
				email,
				password,
				activationCode: code,
				isBackupCode: useBackupCode
			})
			.then((res) => {
				const { status, data } = res;
				if (status === 200) {
					dispatch(setUserDetails({ mfaTypes: 'none' }));
					showSuccess();
					modal.remove();
				} else {
					showError(data.code);
				}
			})
			.catch((error) => {
				showError({
					message: error.response?.data?.code
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const closeBackupCodes = () => {
		setValue([null, null, null, null, null, null]);
		setUseBackupCode(false);
	};

	const nextTab = () => {
		setTab(tab + 1);
	};

	return (
		<Modal maxWidth="small" className="modal-2FA">
			<ModalTitle>
				<Typography variant="p2" fontWeight="500">
					twoFactorModal.disable2FATitle
				</Typography>
			</ModalTitle>
			<ModalContent>
				{tab === 1 && (
					<S.TwoFactorDisableWarningContainer>
						<Image
							url="https://storage.googleapis.com/tryoto-public/public/assets/icons/disable-icon.svg"
							width="120px"
							height="120px"
						/>
						<Typography variant="p1" fontWeight="500" noWrap>
							twoFactorModal.disable2FAWarningTitle
						</Typography>
						<Typography color="var(--content-2)" noWrap>
							twoFactorModal.disable2FAWarningDescription
						</Typography>
					</S.TwoFactorDisableWarningContainer>
				)}
				{tab === 2 && (
					<S.TwoFactorDisableModalContainer>
						<FormItem>
							<Typography className="mb-3" fontWeight="500">
								twoFactorModal.disable2FADescription
							</Typography>
						</FormItem>
						{/*
						 * DON'T REMOVE THIS FORM TAG. THIS IS USED CHROME AUTOFILL BUG FIX!
						 */}
						<form method="dialog">
							<FormItem label="twoFactorModal.emailTitle">
								<StringInput value={email} disabled />
							</FormItem>
							<FormItem label="twoFactorModal.passwordTitle" loading={loading}>
								<PasswordInput value={password} setValue={setPassword} />
							</FormItem>
						</form>
					</S.TwoFactorDisableModalContainer>
				)}
				{tab === 3 && (
					<S.TwoFactorDisable2FAContainer>
						<FormItem>
							<Typography fontWeight="500" noWrap>
								{useBackupCode
									? 'twoFactorModal.disable2FABackupCodesDescription'
									: 'twoFactorModal.disable2FADescription2'}
							</Typography>
						</FormItem>
						<OTPInput
							value={value}
							setValue={setValue}
							length={6}
							onFilledOnce={submit}
							loading={loading}
						/>
						{!useBackupCode && (
							<S.MFABackupCodeButtonContainer>
								<Typography>login.cantAccessMFA</Typography>
								<S.ButtonLink
									variant="transparent"
									label="login.useABackupCode"
									onClick={() => {
										setUseBackupCode(true);
										setValue([null, null, null, null, null, null]);
									}}
								/>
							</S.MFABackupCodeButtonContainer>
						)}
					</S.TwoFactorDisable2FAContainer>
				)}
			</ModalContent>
			<ModalActions>
				<Button
					variant="blackOutline"
					label="cancel"
					onClick={modal.remove}
					css={css`
						text-decoration: underline;
						border: none !important;
					`}
				/>
				{useBackupCode && tab === 3 && (
					<Button
						label="twoFactorModal.previous"
						onClick={closeBackupCodes}
						icon={faChevronLeft}
						variant="outline"
					/>
				)}
				{tab === 1 && <Button label="twoFactorModal.continue" onClick={nextTab} loading={loading} />}
				{tab === 2 && <Button label="twoFactorModal.continue" onClick={login} loading={loading} />}
				{tab === 3 && (
					<Button
						label="twoFactorModal.disable2FAButton"
						color="var(--red)"
						onClick={submit}
						loading={loading}
						disabled={value.includes(null)}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default TwoFactorDisableModal;
